import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@fastnorth/i18next';
import { Div } from '@fastnorth/kraken/components/layout';
import {
  SubEventProgressRowKind,
  SubEventProgressTimeKind,
  type SubEventProgressRow,
} from '../types';
import formatProgressDisplayValue from '../utils/formatProgressDisplayValue';
import formatProgressTime from '../utils/formatProgressTime';
import { PassedCellCheckpoint, PassedCellInBetween } from './PassedCell';
import {
  splitTableCellVariants,
  splitTableCheckpointCellVariants,
  splitTableCheckpointLabelVariants,
  splitTableRowVariants,
  splitTableTimeLabelVariants,
} from './SplitTable.styles';

export type SplitTableRowProps = {
  locale: Locale;
  row: SubEventProgressRow;
  timeZone: string;
};

const formatLastTimeLabel = (
  isoTime: string | null,
  timeKind: SubEventProgressTimeKind,
  locale: Locale,
  timeZone: string,
  estimatedSuffix: string
): string => {
  const formattedTime = formatProgressTime(isoTime, locale, timeZone);
  if (!formattedTime) {
    return formatProgressDisplayValue(null, locale);
  }

  if (timeKind === SubEventProgressTimeKind.PREDICTED) {
    return `${formattedTime} ${estimatedSuffix}`;
  }

  return formattedTime;
};

const SplitTableRow = ({ locale, row, timeZone }: SplitTableRowProps): ReactElement => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.event_progress_v2' });
  const isInBetween = row.kind === SubEventProgressRowKind.IN_BETWEEN;
  const checkpointKind = isInBetween ? 'inBetween' : 'checkpoint';
  const firstTime = formatProgressTime(row.firstTime, locale, timeZone);
  const lastTime = formatLastTimeLabel(
    row.lastTime,
    row.lastTimeKind,
    locale,
    timeZone,
    t('estimated_time_suffix')
  );
  const lastTimeIsEstimated =
    row.lastTimeKind === SubEventProgressTimeKind.PREDICTED && row.lastTime;

  return (
    <Div
      className={splitTableRowVariants({ tone: isInBetween ? 'inBetween' : 'default' })}
      data-testid={`split-table-row-${row.label}`}
      data-row-kind={row.kind}>
      <Div
        className={`${splitTableCellVariants()} ${splitTableCheckpointCellVariants({ kind: checkpointKind })}`}>
        <span className={splitTableCheckpointLabelVariants({ kind: checkpointKind })}>
          {row.label}
        </span>
      </Div>
      <Div className={splitTableCellVariants()}>
        {isInBetween ? (
          <PassedCellInBetween
            gapCount={row.gapCount}
            gapPercentage={row.gapPercentage}
            locale={locale}
          />
        ) : (
          <PassedCellCheckpoint
            locale={locale}
            passedCount={row.passedCount}
            passedPercentage={row.passedPercentage}
            startedCount={row.startedCount}
          />
        )}
      </Div>
      <Div className={splitTableCellVariants()}>
        <span className={splitTableTimeLabelVariants({ emphasis: 'default' })}>
          {firstTime ?? formatProgressDisplayValue(null, locale)}
        </span>
      </Div>
      <Div className={splitTableCellVariants()}>
        <span
          className={splitTableTimeLabelVariants({
            emphasis: lastTimeIsEstimated ? 'estimated' : 'default',
          })}>
          {lastTime}
        </span>
      </Div>
    </Div>
  );
};

export default SplitTableRow;
